
import { Dumb } from './dumb'

import { integrate } from './integrate'

function lerp(x: number, x0: number, x1: number, y0: number, y1: number) {
  return y0 + (x - x0) * (y1 - y0) / (x1 - x0)
}

class Solidus {
  T: number[];
  P: number[];
  constructor(T: number[], P: number[]) {
    this.T = T
    this.P = P
  }
  static linear(T0: number, dTdP: number) {
    const P = [0, 16]
    const T = P.map(p => T0 + p * dTdP)
    return new Solidus(T, P)
  }
  static poly(c: number, b: number, a: number) {
    const P = Array.from(range(0, 16, 0.1));
    const T = P.map((p: number) => c + (b * p) + (a * p * p))
    return new Solidus(T, P)
  }
  static xy(val: any) {
    const T = val.map((v: any) => v[0])
    const P = val.map((v: any) => v[1])
    return new Solidus(T, P)
  }
  add(dT: number) {
    const T = this.T.map(T => T + dT)
    const P = this.P.map(P => P)
    return new Solidus(T, P)
  }
  t(p: number | number[]) {
    if (Array.isArray(p)) {
      return p.map((p: number) => this.t(p))
    }
    if (p < this.P[0])
      return undefined
    for (let i = 1; i < this.P.length; i++) {
      if (p <= this.P[i]) {
        return lerp(p, this.P[i - 1], this.P[i], this.T[i - 1], this.T[i])
      }
    }
    //console.log(p, this.P.at(-1), 'too large')
    return undefined
  }
}

class Adibat {
  Tp: number;
  dTdP: number;
  constructor(Tp: number, dTdP: number) {
    this.Tp = Tp
    this.dTdP = dTdP
  }
  at(P: number) {
    return this.Tp + P * this.dTdP
  }
}

function* range(start: number, stop: number, step: number = 1) {
  if (stop == null) {
    // one param defined
    stop = start;
    start = 0;
  }
  for (let i = start; step > 0 ? i < stop : i > stop; i += step) {
    yield i;
  }
}

const PMin = 0.0
const PMax = 6.1
const TMin = 900
const TMax = 2200

const GPa2Km = 30
const dP = 0.10
const P = Array.from(range(PMin, PMax, dP)) // GPa


let eclogite = Solidus.linear(920, 130)
let eclogite_liq = Solidus.linear(920 + 500, 130)

//let peridotite = Solidus.linear(1150, 120)
//let peridotite_liq = Solidus.linear(1150 + 500, 120)

let peridotite = Solidus.poly(1120.66061, 132.899012, -5.1404654)
let peridotite_liq = peridotite.add(500)

// Green 1973 Figure 1 (PH2O = PTotal) Pyrolite with 6% H2O
let peridotite_h2o = Solidus.xy([
  [1067.857142857143, 0.00546875],
  [1044.642857142857, 0.0164063],
  [1025, 0.04375],
  [1010.7142857142858, 0.0875],
  [998.2142857142858, 0.158594],
  [989.2857142857143, 0.235156],
  [983.9285714285714, 0.339062],
  [980.3571428571429, 0.464844],
  [975, 0.667188],
  [971.4285714285714, 0.858594],
  [967.8571428571429, 1.08828],
  [964.2857142857143, 1.26875],
  [960.7142857142858, 1.49844],
  [953.5714285714286, 1.71719],
  [951.7857142857143, 1.87578],
  [953.5714285714286, 2.02344],
  [960.7142857142858, 2.19297],
  [975, 2.42813],
  [983.9285714285714, 2.60312],
  [1000, 2.80547],
  [1023.2142857142858, 3.05703],
  [1048.2142857142858, 3.27578],
  [1078.5714285714287, 3.50547],
])

let eclogite_co2 = Solidus.xy([
  [920, 0],
  [1100.0649819494586, 1.4],
  [1120.0649819494586, 2.0],
  [1100.0649819494586, 2.5],
  [1066.0649819494586, 2.9487666034155597],
  [1044.404332129964, 3.161290322580645],
  [1027.0758122743682, 3.4269449715370017],
  [1016.9675090252708, 3.8519924098671727],
  [1016.9675090252708, 4.303605313092979],
  [1024.187725631769, 4.72865275142315],
  [1041.5162454873646, 5.286527514231499],
  [1067.5090252707582, 5.924098671726755],
  [1102.1660649819494, 6.588235294117647],
  [1128.158844765343, 7.013282732447818],
  [1161.3718411552347, 7.518026565464895],
  [1196.0288808664259, 7.996204933586338],
  [1242.2382671480145, 8.633776091081595],
  [1284.115523465704, 9.244781783681214],
  [1334.6570397111914, 10.094876660341557],
  [1373.6462093862815, 10.918406072106261],
  [1650, 13],
  [1850, 15.5],
])
let eclogite_co2_liq = eclogite_co2.add(500)

let peridotite_co2 = Solidus.xy([
  [1425, 0],
  [1200, 2],
  [1171.4801444043321, 2.842504743833017],
  [1188.8086642599278, 4.0379506641366225],
  [1274.0072202166066, 6.375711574952562],
  [1370.7581227436824, 8.28842504743833],
  [1457.4007220216608, 9.722960151802656],
  [1561.3718411552347, 11.077798861480076],
  [1850, 14.5],
  [1940, 15.5],
])
let peridotite_co2_liq = peridotite_co2.add(500)

let T_eck = eclogite.t(P)
let T_eck_liq = eclogite_liq.t(P)

let T_eck_co2 = eclogite_co2.t(P)
let T_eck_co2_liq = eclogite_co2_liq.t(P)

let T_per = peridotite.t(P)
let T_per_liq = peridotite_liq.t(P)

let T_per_h2o = peridotite_h2o.t(P)
let T_per_h2o_liq = peridotite_liq.t(P)

let T_per_co2 = peridotite_co2.t(P)
let T_per_co2_liq = peridotite_co2_liq.t(P)

const Hf = 100 // cal/gm
const Cp = 0.3 // cal/gm/C

function melt(P: number | number[], adiabat: any, solidus: any, liquidus: any) {
  if (Array.isArray(P)) {
    const TF = P.map((p: number) => melt(p, adiabat, solidus, liquidus))
    const T = TF.map((v: any) => v.T)
    const F = TF.map((v: any) => v.F)
    return { T, F }
  }

  let Ta = adiabat.at(P)
  const Ts = solidus.t(P)
  const Tl = liquidus.t(P)

  // Eq (17) (Ta-Ts) = F (Hf/Cp) + F(dT/dF)
  //         (Ta-Ts) = F (Hf/Cp + dTdF)
  //         F = (Ts-Ta) / (Hf/Cp + dTdF)

  const dTdF = (Tl - Ts) / (1.0 - 0.0)
  const Hf_Cp = Hf / Cp

  if (Ts > Ta)
    return { T: Ta, F: 0 }

  let F = (Ta - Ts) / (Hf_Cp + dTdF)

  let T = Ts + F * dTdF
  if (F > 1) {
    F = 1
    let dT = (Hf_Cp + dTdF)
    T = (Ta - dT) - Ts + Tl
  }

  return { T, F }
}

function melt_F(P: number | number[], adiabat: any, solidus: any, liquidus: any) {
  return melt(P, adiabat, solidus, liquidus).F
}

//const cumSum = (sum => value => sum += value)(0);
function cumSum(vals: number[]) {
  let out = []
  let sum = 0
  for (const v of vals) {
    sum += v
    out.push(sum)
  }
  return out
}
const plt = new Dumb('canvas')
plt.dpad(20, 20, 0, 50)

const Tp_ele = document.getElementById("Tp")
const Tp_value = document.getElementById("Tp_value")
const comp_ele = document.getElementById("comp")

function update(Tp: number) {

  Tp_value.innerHTML = Tp.toString() + " C"
  let A_1350 = new Adibat(Tp, 20)

  let T_1350 = P.map((p: number) => A_1350.at(p))

  plt.clear()
  plt.xlim(TMin, TMax)
  plt.ylim(PMin, PMax)

  plt.xaxis("top")
  plt.xaxis("bottom")
  plt.yaxis()

  plt.xticks(range(TMin, TMax + 1, 200), true, 10)
  plt.xticks(range(TMin, TMax + 1, 50), false, 5)
  plt.yticks(range(PMin, PMax, 1), true, 10)
  plt.yticks(range(PMin, PMax, 0.25), false, 5)

  plt.yaxis("right")

  plt.yticks2(range(0, 200, 50), true, 10, (x) => { return x * 1e3 * 3300 * 9.8 / 1e9 })
  plt.yticks2(range(0, 200, 10), false, 5, (x) => { return x * 1e3 * 3300 * 9.8 / 1e9 })

  const T_eck_melt = melt(P, A_1350, eclogite, eclogite_liq)
  const T_per_melt = melt(P, A_1350, peridotite, peridotite_liq)
  const T_per_h2o_melt = melt(P, A_1350, peridotite_h2o, peridotite_liq)
  const T_eck_co2_melt = melt(P, A_1350, eclogite_co2, eclogite_co2_liq)
  const T_per_co2_melt = melt(P, A_1350, peridotite_co2, peridotite_co2_liq)

  plt.line(T_1350, P, 'darkgray', 2)

  function m2P(v: number) {
    return TMin + v * (TMax - TMin)
  }

  if (comp_ele.value == "eck") {
    plt.line(T_eck, P, 'black', 2)
    plt.line(T_eck_liq, P, 'black', 2)
    plt.line(T_eck_melt.T, P, 'red', 4)
    plt.line(T_eck_melt.F.map(m2P), P, 'blue', 1)
    let F = T_eck_melt.F.reverse()
    let H = cumSum(F).map(v => v * dP * GPa2Km).reverse()
    plt.line(H.map(v => (v / 100) * (TMax - TMin) + TMin), P, 'green', 2)

  }
  if (comp_ele.value == "eck_co2") {
    plt.line(T_eck_co2, P, 'black', 2)
    plt.line(T_eck_co2_liq, P, 'black', 2)
    plt.line(T_eck_co2_melt.T, P, 'red', 4)
    plt.line(T_eck_co2_melt.F.map(m2P), P, 'blue', 1)
    let F = T_eck_co2_melt.F.reverse()
    let H = cumSum(F).map(v => v * dP * GPa2Km).reverse()
    plt.line(H.map(v => (v / 100) * (TMax - TMin) + TMin), P, 'green', 2)

  }
  if (comp_ele.value == "per_co2") {
    plt.line(T_per_co2, P, 'black', 2)
    plt.line(T_per_co2_liq, P, 'black', 2)
    plt.line(T_per_co2_melt.T, P, 'red', 4)
    plt.line(T_per_co2_melt.F.map(m2P), P, 'blue', 1)
    let F = T_per_co2_melt.F.reverse()
    let H = cumSum(F).map(v => v * dP * GPa2Km).reverse()
    plt.line(H.map(v => (v / 100) * (TMax - TMin) + TMin), P, 'green', 2)
  }
  if (comp_ele.value == "per") {
    plt.line(T_per, P, 'black', 2)
    plt.line(T_per_liq, P, 'black', 2)
    plt.line(T_per_melt.T, P, 'red', 4)
    plt.line(T_per_melt.F.map(m2P), P, 'blue', 1)
    let F = T_per_melt.F.reverse()
    let H = cumSum(F).map(v => v * dP * GPa2Km).reverse()
    plt.line(H.map(v => (v / 100) * (TMax - TMin) + TMin), P, 'green', 2)
  }
  if (comp_ele.value == "per_h2o") {
    plt.line(T_per_h2o, P, 'black', 2)
    plt.line(T_per_h2o_liq, P, 'black', 2)
    plt.line(T_per_h2o_melt.T, P, 'red', 4)
    plt.line(T_per_h2o_melt.F.map(m2P), P, 'blue', 1)
    let F = T_per_h2o_melt.F.reverse()
    let H = cumSum(F).map(v => v * dP * GPa2Km).reverse()
    plt.line(H.map(v => (v / 100) * (TMax - TMin) + TMin), P, 'green', 2)
  }

  plt.xticks2(range(0, 1.1, 0.2), true, 10, "bottom", m2P)
  plt.xticks2(range(0, 1.1, 0.05), false, 5, "bottom", m2P)

  let dydp = 1 / (plt.scale.y(1) - plt.scale.y(0))
  plt.xticks2(range(0, 100.1, 20), true, 10, "bottom", (v) => TMin + (v / 100) * (TMax - TMin), dydp * 50)
  plt.xticks2(range(0, 100.1, 5), false, 5, "bottom", (v) => TMin + (v / 100) * (TMax - TMin), dydp * 50)
  plt.xaxis("bottom", 50)

  plt.text('Melt Fraction', plt.width / 2, plt.height - 50, 'bottom', 'center', 'blue')
  plt.text('Crustal Thickness', plt.width / 2, plt.height - 2, 'bottom', 'center', 'green')
  plt.text('Temperature (C)', plt.width / 2, 2, 'top', 'center')
  plt.text('Solidus', plt.width * 0.62, plt.height - 120, 'bottom', 'left', 'black')
  plt.text('Liquidus', plt.width * 0.75, plt.height - 330, 'bottom', 'left', 'black')
  plt.ctx.save()
  plt.ctx.translate(plt.width * 0.96, plt.height / 2);
  plt.ctx.rotate(Math.PI / 2);
  plt.text('Depth (km)', 0, 0, 'bottom', 'center', 'black')
  plt.ctx.restore()
  plt.ctx.save()
  plt.ctx.translate(plt.width * 0.05, plt.height / 2);
  plt.ctx.rotate(-Math.PI / 2);
  plt.text('Pressure (GPa)', 0, 0, 'bottom', 'center', 'black')
  plt.ctx.restore()
}

update(Number(Tp_ele.value))

Tp_ele.addEventListener('input', (_ev: any) => {
  update(Number(Tp_ele.value))
})
comp_ele.addEventListener('change', (_ev: any) => {
  update(Number(Tp_ele.value))
})
